import { UserCheck, CalendarCheck, IndianRupee, CalendarRange } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useSeo, pageSeo, PAGES, clinicSchema, graph, crumbs } from "@/lib/seo";
import { inr } from "@/lib/format";
import { SERVICE_GROUPS } from "@/data/services";
import { PageHero } from "@/components/common/SectionHeading";
import { CtaBand } from "@/components/common/CtaBand";
import { accent } from "@/lib/ui";

const InfoStrip = () => {
  const { settings } = useSite();
  const items = [
    [UserCheck, "In-person consultation"],
    [CalendarCheck, "Appointment required"],
    [IndianRupee, `${inr(settings.consultation_fee)} per visit`],
    [CalendarRange, "Care plans typically run 1 to 3 months, depending on the condition"],
  ];
  return (
    <ul className="grid gap-px overflow-hidden rounded-3xl bg-slate-200 ring-1 ring-slate-200 sm:grid-cols-2 lg:grid-cols-4" data-testid="services-info-strip">
      {items.map(([Icon, label], i) => (
        <li key={label} className="flex items-center gap-3 bg-white p-5">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl shadow-md ${accent(i).icon}`}><Icon className="h-5 w-5" aria-hidden="true" /></span>
          <span className="text-base font-medium text-brand-ink">{label}</span>
        </li>
      ))}
    </ul>
  );
};

const Group = ({ g, i }) => {
  const Icon = g.icon;
  const a = accent(i);
  return (
    <section id={g.id} className="grid gap-6 border-t border-slate-200 py-12 lg:grid-cols-12" aria-labelledby={`${g.id}-title`} data-testid={`service-group-${g.id}`}>
      <div className="lg:col-span-4">
        <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold shadow-md ${a.icon}`}>0{i + 1}</span>
        <h2 id={`${g.id}-title`} className="mt-3 flex items-center gap-3 font-heading text-2xl font-bold text-brand-ink">
          <Icon className={`h-6 w-6 shrink-0 ${a.text}`} aria-hidden="true" /> {g.title}
        </h2>
      </div>
      <ul className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
        {g.items.map(([name, desc]) => (
          <li key={name} className={`rounded-2xl p-5 ring-1 ring-black/5 transition-transform duration-300 hover:-translate-y-0.5 ${a.soft}`}>
            <h3 className="text-base font-bold text-brand-ink">{name}</h3>
            <p className="mt-1 text-base text-slate-600">{desc}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function Services() {
  const { settings } = useSite();
  useSeo({ ...pageSeo("/services"), jsonLd: graph(clinicSchema(settings), crumbs(["Services", "/services"])) });
  return (
    <>
      <PageHero eyebrow="Services" title={PAGES["/services"].h1} intro={PAGES["/services"].intro} />
      <div className="container-x py-12 md:py-16">
        <InfoStrip />
        <div className="mt-12">{SERVICE_GROUPS.map((g, i) => <Group key={g.id} g={g} i={i} />)}</div>
      </div>
      <CtaBand />
    </>
  );
}
